/**
 * TTS Streaming Debug UI
 * Allows testing the WebSocket streaming endpoint with low latency and visual feedback.
 */

import { APIClient } from './api';

export class TTSStreamingDebugUI {
    private container: HTMLElement;
    private client: APIClient;
    private socket: WebSocket | null = null;
    private audioContext: AudioContext | null = null;
    private nextStartTime: number = 0;
    private isStreamActive: boolean = false;
    private animationFrame: number | null = null;

    // Viseme state
    private currentVisemeId: number = 0;
    private visemeQueue: { visemeId: number; time: number }[] = [];
    private streamStartTime: number = 0;

    // Latency metrics
    private requestStartTime: number = 0;
    private firstByteTime: number = 0;

    constructor(container: HTMLElement, client: APIClient) {
        this.container = container;
        this.client = client;
    }

    render(): void {
        this.container.innerHTML = `
      <div class="test-section">
        <h2>TTS Streaming Debug</h2>
        <p class="description">Test real-time WebSocket TTS streaming (24kHz PCM)</p>

        <div class="tts-container">
          <label for="stream-text-input">Enter text to stream:</label>
          <textarea id="stream-text-input" placeholder="Hello, I am streaming voice in real-time." rows="3"></textarea>

          <div class="controls">
            <div class="control-group">
               <label>Provider:</label>
               <select id="stream-provider">
                 <option value="azure" selected>Azure</option>
                 <option value="elevenlabs">ElevenLabs</option>
               </select>
            </div>

            <div class="control-group">
              <label>Voice:</label>
              <select id="stream-voice">
                 <!-- Populated dynamically -->
              </select>
            </div>

            <div class="control-group" id="stream-emotion-group">
               <label>Emotion (Azure):</label>
               <select id="stream-emotion">
                 <option value="neutral">Neutral</option>
                 <option value="cheerful">Cheerful</option>
                 <option value="sad">Sad</option>
                 <option value="excited">Excited</option>
                 <option value="gentle">Gentle</option>
               </select>
            </div>

            <div class="control-group">
              <label for="stream-rate">Speed (Rate): <span id="stream-rate-value">1.0</span>x</label>
              <input type="range" id="stream-rate" min="0.5" max="2.0" step="0.1" value="1.0">
            </div>

            <div class="control-group">
              <label for="stream-pitch">Pitch: <span id="stream-pitch-value">0</span>%</label>
              <input type="range" id="stream-pitch" min="-50" max="50" step="1" value="0">
            </div>

            <div class="control-group" id="stream-model-group" style="display: none;">
              <label>Model:</label>
              <select id="stream-model">
                <option value="eleven_flash_v2_5">Flash v2.5 (Fastest)</option>
                <option value="eleven_turbo_v2_5">Turbo v2.5</option>
                <option value="eleven_multilingual_v2">Multilingual v2</option>
              </select>
            </div>

            <div class="elevenlabs-params" id="stream-elevenlabs-params" style="display: none;">
              <div class="control-group">
                <label for="stream-stability">Stability: <span id="stream-stability-value">0.5</span></label>
                <input type="range" id="stream-stability" min="0" max="1" step="0.01" value="0.5">
              </div>
              <div class="control-group">
                <label for="stream-similarity">Similarity: <span id="stream-similarity-value">0.75</span></label>
                <input type="range" id="stream-similarity" min="0" max="1" step="0.01" value="0.75">
              </div>
              <div class="control-group">
                <label for="stream-style">Style: <span id="stream-style-value">0.0</span></label>
                <input type="range" id="stream-style" min="0" max="1" step="0.01" value="0.0">
              </div>
              <div class="control-group">
                <label>
                  <input type="checkbox" id="stream-speaker-boost" checked>
                  Speaker Boost
                </label>
              </div>
            </div>
          </div>

          <div class="button-group">
            <button id="connect-btn" class="btn btn-success">Connect</button>
            <button id="disconnect-btn" class="btn btn-danger" disabled>Disconnect</button>
            <button id="stream-btn" class="btn btn-primary" disabled>Start Stream</button>
            <button id="stop-btn" class="btn btn-warning" disabled>Stop Stream</button>
          </div>

          <div class="control-group" style="margin-top: 10px;">
            <label>
              <input type="checkbox" id="stream-force">
              Force (Bypass Cache)
            </label>
          </div>
        </div>

        <div id="stream-status" class="status-panel" style="margin-top: 20px; padding: 15px; background: #f0f0f0; border-radius: 8px;">
            <div><strong>Status:</strong> <span id="connection-status">Disconnected</span></div>
            <div><strong>Latency (TTFB):</strong> <span id="latency-metric">--</span> ms</div>
        </div>

        <div class="visualization-container" style="display: flex; gap: 20px; align-items: center; margin-top: 20px;">
           <div class="face-visualizer" style="position: relative; width: 200px; height: 200px; background: #fff; border: 1px solid #ccc; border-radius: 50%;">
              <canvas id="stream-face-canvas" width="200" height="200"></canvas>
           </div>

           <div class="viseme-log" style="flex: 1; height: 200px; overflow-y: auto; background: #1a1a1a; color: #0f0; font-family: monospace; padding: 10px; border-radius: 4px; font-size: 12px;">
              <div id="viseme-log-content"></div>
           </div>
        </div>
      </div>
    `;

        this.setupEventListeners();
        this.updateVoiceList();
        this.startRenderLoop();
    }

    destroy(): void {
        this.stopStream();
        if (this.audioContext) {
            this.audioContext.close();
            this.audioContext = null;
        }
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
        }
        this.container.innerHTML = '';
    }

    private setupEventListeners() {
        const connectBtn = document.getElementById('connect-btn')!;
        const disconnectBtn = document.getElementById('disconnect-btn')!;
        const streamBtn = document.getElementById('stream-btn')!;
        const stopBtn = document.getElementById('stop-btn')!;
        const providerSelect = document.getElementById('stream-provider')!;

        // Slider value updates
        const rateSlider = document.getElementById('stream-rate') as HTMLInputElement;
        const pitchSlider = document.getElementById('stream-pitch') as HTMLInputElement;
        const stabilitySlider = document.getElementById('stream-stability') as HTMLInputElement;
        const similaritySlider = document.getElementById('stream-similarity') as HTMLInputElement;
        const styleSlider = document.getElementById('stream-style') as HTMLInputElement;

        rateSlider?.addEventListener('input', () => {
            document.getElementById('stream-rate-value')!.textContent = rateSlider.value;
        });
        pitchSlider?.addEventListener('input', () => {
            document.getElementById('stream-pitch-value')!.textContent = pitchSlider.value;
        });
        stabilitySlider?.addEventListener('input', () => {
            document.getElementById('stream-stability-value')!.textContent = stabilitySlider.value;
        });
        similaritySlider?.addEventListener('input', () => {
            document.getElementById('stream-similarity-value')!.textContent = similaritySlider.value;
        });
        styleSlider?.addEventListener('input', () => {
            document.getElementById('stream-style-value')!.textContent = styleSlider.value;
        });

        connectBtn.addEventListener('click', () => this.connectWebSocket());
        disconnectBtn.addEventListener('click', () => this.disconnectWebSocket());
        streamBtn.addEventListener('click', () => this.startStream());
        stopBtn.addEventListener('click', () => this.stopStream());
        providerSelect.addEventListener('change', () => {
            this.updateVoiceList();
            this.toggleElevenLabsParams();
        });
    }

    private toggleElevenLabsParams() {
        const providerSelect = document.getElementById('stream-provider') as HTMLSelectElement;
        const elevenLabsParams = document.getElementById('stream-elevenlabs-params')!;
        const modelGroup = document.getElementById('stream-model-group')!;
        const emotionGroup = document.getElementById('stream-emotion-group')!;

        if (providerSelect.value === 'elevenlabs') {
            elevenLabsParams.style.display = 'block';
            modelGroup.style.display = 'block';
            emotionGroup.style.display = 'none'; // Hide emotion for ElevenLabs
        } else {
            elevenLabsParams.style.display = 'none';
            modelGroup.style.display = 'none';
            emotionGroup.style.display = 'block'; // Show emotion for Azure
        }
    }

    private updateVoiceList() {
        const providerSelect = document.getElementById('stream-provider') as HTMLSelectElement;
        const voiceSelect = document.getElementById('stream-voice') as HTMLSelectElement;
        const provider = providerSelect.value;

        voiceSelect.innerHTML = '';

        if (provider === 'azure') {
            const voices = [
                { value: "en-US-JennyNeural", label: "English (US) - Jenny" },
                { value: "en-US-GuyNeural", label: "English (US) - Guy" },
                { value: "ja-JP-NanamiNeural", label: "Japanese - Nanami" },
                { value: "ja-JP-KeitaNeural", label: "Japanese - Keita" }
            ];
            voices.forEach(v => {
                const opt = document.createElement('option');
                opt.value = v.value;
                opt.textContent = v.label;
                voiceSelect.appendChild(opt);
            });
        } else {
            const voices = [
                { value: "21m00Tcm4TlvDq8ikWAM", label: "Rachel" },
                { value: "ozfS3gQtjFX3kQyJ12dX", label: "Saori (JP)" }
            ];
            voices.forEach(v => {
                const opt = document.createElement('option');
                opt.value = v.value;
                opt.textContent = v.label;
                voiceSelect.appendChild(opt);
            });
        }
    }

    private async connectWebSocket() {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('[TTS Debug] Already connected');
            return;
        }

        // Ensure token is fresh
        await this.client.refreshToken().catch(e => console.warn("Failed to refresh token before WS", e));

        const token = this.client.getToken();
        if (!token) {
            alert('Please sign in first');
            return;
        }

        const baseUrl = (this.client as any)['baseUrl'].replace(/^http/, 'ws') + '/v1/tts/stream';
        const wsUrl = `${baseUrl}?access_token=${token}`;

        console.log('[TTS Debug] Connecting to WebSocket...');
        this.updateStatus('Connecting...');

        this.socket = new WebSocket(wsUrl);
        this.socket.binaryType = 'arraybuffer';

        const connectStart = Date.now();

        this.socket.onopen = () => {
            const connectTime = Date.now() - connectStart;
            console.log(`[TTS Debug] WebSocket connected in ${connectTime}ms`);
            this.updateStatus('Connected (Ready)');
            this.updateButtons();
        };

        this.socket.onmessage = (event) => {
            try {
                if (event.data instanceof ArrayBuffer) {
                    this.handleAudioFrame(event.data);
                } else if (typeof event.data === 'string') {
                    const msg = JSON.parse(event.data);
                    this.handleMessage(msg);
                }
            } catch (e) {
                console.error('Failed to parse message:', e);
            }
        };

        this.socket.onclose = (event) => {
            console.log('[TTS Debug] WebSocket closed:', event.code, event.reason);
            this.updateStatus('Disconnected');
            this.updateButtons();
            this.isStreamActive = false;
        };

        this.socket.onerror = (e) => {
            console.error('WebSocket Error:', e);
            this.updateStatus('Error');
            this.updateButtons();
        };
    }

    private disconnectWebSocket() {
        if (this.socket) {
            console.log('[TTS Debug] Disconnecting WebSocket...');
            this.socket.close();
            this.socket = null;
        }
        this.isStreamActive = false;
        this.updateButtons();
    }

    private updateButtons() {
        const connectBtn = document.getElementById('connect-btn') as HTMLButtonElement;
        const disconnectBtn = document.getElementById('disconnect-btn') as HTMLButtonElement;
        const streamBtn = document.getElementById('stream-btn') as HTMLButtonElement;
        const stopBtn = document.getElementById('stop-btn') as HTMLButtonElement;

        const isConnected = !!(this.socket && this.socket.readyState === WebSocket.OPEN);

        connectBtn.disabled = isConnected;
        disconnectBtn.disabled = !isConnected;
        streamBtn.disabled = !isConnected || this.isStreamActive;
        stopBtn.disabled = !this.isStreamActive;
    }

    private async startStream() {
        const textInput = document.getElementById('stream-text-input') as HTMLTextAreaElement;
        const text = textInput.value.trim();
        if (!text) {
            alert('Please enter text');
            return;
        }

        const providerSelect = document.getElementById('stream-provider') as HTMLSelectElement;
        const voiceSelect = document.getElementById('stream-voice') as HTMLSelectElement;
        const emotionSelect = document.getElementById('stream-emotion') as HTMLSelectElement;
        const rateInput = document.getElementById('stream-rate') as HTMLInputElement;
        const pitchInput = document.getElementById('stream-pitch') as HTMLInputElement;

        // ElevenLabs parameters
        const stabilityInput = document.getElementById('stream-stability') as HTMLInputElement;
        const similarityInput = document.getElementById('stream-similarity') as HTMLInputElement;
        const styleInput = document.getElementById('stream-style') as HTMLInputElement;
        const speakerBoostInput = document.getElementById('stream-speaker-boost') as HTMLInputElement;
        const modelSelect = document.getElementById('stream-model') as HTMLSelectElement;

        // Don't stop existing connection - reuse it for better performance
        // this.stopStream(); // Removed - keep connection alive

        // Initialize Audio Context (must be user gesture)
        if (!this.audioContext || this.audioContext.state === 'closed') {
            this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
        } else if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
        }

        this.nextStartTime = this.audioContext.currentTime + 0.1; // Buffer slightly
        this.visemeQueue = [];

        // Check if WebSocket is already connected - reuse it!
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            console.log('[TTS Debug] Reusing existing WebSocket connection - skipping 2s overhead');
            this.requestStartTime = Date.now();
            this.firstByteTime = 0;

            // Send synthesis request on existing connection
            const provider = providerSelect.value;
            const forceCheckbox = document.getElementById('stream-force') as HTMLInputElement;

            const payload: any = {
                action: 'synthesize',
                text: text,
                provider: provider,
                language: provider === 'azure' && voiceSelect.value.startsWith('ja') ? 'ja-JP' : 'en-US',
                voice: voiceSelect.value,
                emotion: emotionSelect.value,
                rate: parseFloat(rateInput.value),
                pitch: parseInt(pitchInput.value, 10),
                visemeType: 'facial-expression',
                force: forceCheckbox.checked
            };

            if (provider === 'elevenlabs') {
                payload.modelId = modelSelect.value || 'eleven_flash_v2_5';
                payload.stability = parseFloat(stabilityInput.value);
                payload.similarityBoost = parseFloat(similarityInput.value);
                payload.style = parseFloat(styleInput.value);
                payload.useSpeakerBoost = speakerBoostInput.checked;
            }

            this.socket.send(JSON.stringify(payload));
            console.log('[TTS Debug] Request sent on existing connection');
            return;
        }

        // Connect WebSocket (first time only)
        console.log('[TTS Debug] Connecting to WebSocket...');
        // Ensure token is fresh
        await this.client.refreshToken().catch(e => console.warn("Failed to refresh token before WS", e));

        const token = this.client.getToken();
        if (!token) {
            alert('Please sign in first');
            return;
        }

        // Use access_token query param for WebSocket auth (browser limitation - can't set Authorization header)
        // Gateway will validate token and pass SignedContext via X-Signed-Context header to TTS service
        const baseUrl = (this.client as any)['baseUrl'].replace(/^http/, 'ws') + '/v1/tts/stream';
        const wsUrl = `${baseUrl}?access_token=${token}`;

        console.log('[TTS Debug] Connecting to WebSocket...');

        this.updateStatus('Connecting...');
        this.socket = new WebSocket(wsUrl);
        this.socket.binaryType = 'arraybuffer'; // Enable binary audio frames

        this.requestStartTime = Date.now();
        this.firstByteTime = 0;

        this.socket.onopen = () => {
            const connectTime = Date.now() - this.requestStartTime;
            console.log(`[TTS Debug] WebSocket connected in ${connectTime}ms`);

            this.updateStatus('Connected. Sending request...');
            this.updateButtons();

            const provider = providerSelect.value;
            const forceCheckbox = document.getElementById('stream-force') as HTMLInputElement;

            const payload: any = {
                action: 'synthesize',
                text: text,
                provider: provider,
                language: provider === 'azure' && voiceSelect.value.startsWith('ja') ? 'ja-JP' : 'en-US',
                voice: voiceSelect.value,
                emotion: emotionSelect.value,
                rate: parseFloat(rateInput.value),
                pitch: parseInt(pitchInput.value, 10),
                visemeType: 'facial-expression',
                force: forceCheckbox.checked // Bypass cache if checked
            };

            // Add ElevenLabs-specific parameters
            if (provider === 'elevenlabs') {
                payload.modelId = modelSelect.value || 'eleven_flash_v2_5';
                payload.stability = parseFloat(stabilityInput.value);
                payload.similarityBoost = parseFloat(similarityInput.value);
                payload.style = parseFloat(styleInput.value);
                payload.useSpeakerBoost = speakerBoostInput.checked;
            }

            this.socket?.send(JSON.stringify(payload));
            const sentTime = Date.now() - this.requestStartTime;
            console.log(`[TTS Debug] Request sent at ${sentTime}ms`);
        };

        this.socket.onmessage = (event) => {
            try {
                if (event.data instanceof ArrayBuffer) {
                    // Binary Audio Frame
                    this.handleAudioFrame(event.data);
                } else if (typeof event.data === 'string') {
                    // JSON Control Frame
                    const msg = JSON.parse(event.data);
                    this.handleMessage(msg);
                }
            } catch (e) {
                console.error('Failed to parse message:', e);
            }
        };

        this.socket.onclose = () => {
            this.updateStatus('Disconnected');
            this.updateButtons();
            this.isStreamActive = false;
        };

        this.socket.onerror = (e) => {
            console.error('WebSocket Error:', e);
            this.updateStatus('Error');
        };

        this.isStreamActive = true;
        this.streamStartTime = this.audioContext.currentTime;
    }

    private stopStream() {
        // Send cancel action instead of closing connection
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
            this.socket.send(JSON.stringify({ action: 'cancel' }));
            console.log('[TTS Debug] Sent cancel request (keeping connection alive)');
        }
        // Don't close socket - keep connection alive for reuse
        // this.socket.close();
        // this.socket = null;

        this.isStreamActive = false;
        this.updateButtons();
    }



    private handleAudioFrame(buffer: ArrayBuffer) {
        if (!this.firstByteTime) {
            this.firstByteTime = Date.now();
            const latency = this.firstByteTime - this.requestStartTime;
            document.getElementById('latency-metric')!.textContent = latency.toString();
        }

        // Convert to Base64 to reuse existing processAudio?? 
        // No, processAudio does `atob`. Let's refactor processAudio to take ArrayBuffer.
        this.processAudio(buffer);
    }

    private handleMessage(msg: any) {
        if (msg.type === 'start') {
            const startMessageTime = Date.now() - this.requestStartTime;
            console.log(`[TTS Debug] Received 'start' message at ${startMessageTime}ms`);

            this.updateStatus('Streaming...');
            this.visemeQueue = [];
            this.firstByteTime = 0;
            // Reset synchronization time origin
            if (this.audioContext) {
                // The stream effectively starts "now" relative to playback queuing
                // A more accurate sync would be when the first audio chunk arrives
            }
        } else if (msg.type === 'audio') {
            // Legacy JSON audio handling (should be removed or kept as fallback)
            console.warn("Received legacy JSON audio frame");
            // if (!this.firstByteTime) { ... }
            // this.processAudio(msg.data); // msg.data is base64 string
        } else if (msg.type === 'viseme') {
            // Store viseme with timestamp (msg.time is in seconds)
            this.visemeQueue.push({
                visemeId: msg.visemeId,
                time: msg.time
            });
            // Log it
            this.logViseme(msg.visemeId, msg.time * 1000);
        } else if (msg.type === 'done') {
            console.log('[TTS Debug] Stream done (keeping connection alive)');
            this.isStreamActive = false;
            this.updateButtons();
            // Don't close socket - keep connection for reuse
        } else if (msg.type === 'complete') {
            console.log('[TTS Debug] Synthesis complete (keeping connection alive)');
            this.isStreamActive = false;
            this.updateButtons();
        } else if (msg.type === 'error') {
            this.updateStatus('Error: ' + msg.message);
            alert('Stream Error: ' + msg.message);
        }
    }

    private processAudio(data: string | ArrayBuffer) {
        if (!this.audioContext) return;

        let buffer: ArrayBuffer;

        if (typeof data === 'string') {
            // Base64 (Legacy)
            const binary = atob(data);
            const len = binary.length;
            buffer = new ArrayBuffer(len);
            const view = new DataView(buffer);
            for (let i = 0; i < len; i++) {
                view.setUint8(i, binary.charCodeAt(i));
            }
        } else {
            // ArrayBuffer (Native)
            buffer = data;
        }

        // Int16 Array
        const int16 = new Int16Array(buffer);
        const float32 = new Float32Array(int16.length);

        // Normalize Int16 to Float32 [-1.0, 1.0]
        for (let i = 0; i < int16.length; i++) {
            float32[i] = int16[i] / 32768.0;
        }

        // Create AudioBuffer
        const audioBuf = this.audioContext.createBuffer(1, float32.length, 24000);
        audioBuf.copyToChannel(float32, 0);

        // Schedule Playback
        const source = this.audioContext.createBufferSource();
        source.buffer = audioBuf;
        source.connect(this.audioContext.destination);

        // Schedule with gap detection
        const now = this.audioContext.currentTime;
        if (this.nextStartTime < now) {
            const gap = now - this.nextStartTime;
            if (gap > 0.1) { // Log gaps > 100ms
                console.warn(`[Audio] Scheduling gap detected: ${(gap * 1000).toFixed(0)}ms behind`);
            }
            this.nextStartTime = now;
        }

        source.start(this.nextStartTime);
        this.nextStartTime += audioBuf.duration;

        // Log scheduling info
        console.log(`[Audio] Scheduled ${int16.length} samples (${audioBuf.duration.toFixed(3)}s) at ${this.nextStartTime.toFixed(3)}s`);
    }

    private startRenderLoop() {
        const loop = () => {
            this.animationFrame = requestAnimationFrame(loop);
            this.draw();
        };
        this.animationFrame = requestAnimationFrame(loop);
    }

    private draw() {
        if (!this.audioContext) return;

        // Determine current stream time (approximate)
        // We need to know "playback time relative to start"
        // Since we schedule chunks, `currentTime` is reliable if we track when playback *actually* started.
        // Simplifying: Use `nextStartTime - bufferedDuration`? No.
        // Better: Match `audioContext.currentTime` against our expected timeline.
        // But `viseme.time` is relative to stream start.
        // When did stream start playing?
        // Let's approximate: `streamPlayStart = nextStartTime (initial)`.
        // Actually, `nextStartTime` increments.
        // The *first* chunk was scheduled at `initialNextStartTime`.
        // So `streamTime = audioContext.currentTime - initialNextStartTime`.

        // Need to store `initialPlayTime`.
        // I'll hack it: If `isStreamActive`, just show the "latest" viseme that matches `now`.
        // But I haven't stored `initialPlayTime`.
        // Let's assume latency is constant and just show recent visemes for debug.

        // For proper sync:
        // When `firstByte` arrives, we set `playbackStartTime = nextStartTime (which was set to now + 0.1)`.
        // THEN `currentStreamTime = audioContext.currentTime - playbackStartTime`.

        // I'll skip complex sync for this debug tool and just render the "last received" viseme for now?
        // No, that's instantaneous. Visemes arrive way ahead of audio.
        // I will try to estimate `currentStreamTime`.
        // Let's initialize `playbackStartTime` when the first chunk is scheduled.

        // ... (Simplified logic for now: just basic render loop)
        const canvas = document.getElementById('stream-face-canvas') as HTMLCanvasElement;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Clear
        ctx.clearRect(0, 0, 200, 200);
        const cx = 100, cy = 100;

        // Draw Face
        ctx.beginPath();
        ctx.arc(cx, cy, 90, 0, Math.PI * 2);
        ctx.fillStyle = '#fff';
        ctx.fill();
        ctx.stroke();

        // Eyes
        ctx.fillStyle = '#333';
        ctx.beginPath();
        ctx.arc(cx - 30, cy - 20, 10, 0, Math.PI * 2);
        ctx.arc(cx + 30, cy - 20, 10, 0, Math.PI * 2);
        ctx.fill();

        // Mouth
        // Find active viseme based on time matching?
        // Without strict sync, I'll just look for a viseme that matches the "current time" if I could track it.
        // Since I didn't save `playbackStartTime`, I'll update it next revision.
        // For now, I'll just draw a generic closed mouth or the last received viseme ID (debug).

        // Using `this.currentVisemeId` which I update in `handleMessage`?
        // No, `handleMessage` receives them in burst.
        // I need to pop them from queue based on time.

        // Let's use `Date.now()` vs `firstByteTime` as a rough clock for the stream?
        if (this.firstByteTime > 0) {
            const elapsed = (Date.now() - this.firstByteTime) / 1000;
            // Find matching viseme
            const match = this.visemeQueue.find(v => Math.abs(v.time - elapsed) < 0.1); // 100ms window
            if (match) {
                this.currentVisemeId = match.visemeId;
            }
        } else {
            this.currentVisemeId = 0;
        }

        // Draw Mouth based on ID
        this.drawMouth(ctx, cx, cy + 30, this.currentVisemeId);
    }

    private drawMouth(ctx: CanvasRenderingContext2D, x: number, y: number, id: number) {
        let w = 40, h = 5;
        // Simple mapping for debug
        if (id > 0) h = 20; // Open if ID > 0
        if ([1, 2, 3, 9, 11].includes(id)) h = 30; // Ah
        if ([7, 8].includes(id)) w = 30; // Ooh

        ctx.fillStyle = '#f00';
        ctx.beginPath();
        ctx.ellipse(x, y, w / 2, h / 2, 0, 0, Math.PI * 2);
        ctx.fill();
    }

    private logViseme(id: number, time: number) {
        const log = document.getElementById('viseme-log-content');
        if (log) {
            const div = document.createElement('div');
            div.textContent = `[${time}ms] ID: ${id}`;
            log.appendChild(div);
            log.scrollTop = log.scrollHeight;
        }
    }

    private updateStatus(text: string) {
        document.getElementById('connection-status')!.textContent = text;
    }
}
